import DOMPurify from 'dompurify'
import { EventSource } from 'eventsource'
import { marked } from 'marked'
import { useCallback, useEffect, useRef, useState } from 'react'

import { prepareDefaultHeaders } from '../../../../api'
import keycloakService from '../../../../services/KeycloakService'

export type StreamSummaryStatus = 'idle' | 'streaming' | 'done' | 'cancelled' | 'error'

type StreamSummaryParams = {
  gameId: string
  prompt: string
  promptVersion: number | null
  extraInstructions?: string
  asOfDate?: string
}

enum StreamDataType {
  TEXT = 'TEXT',
  IMAGE = 'IMAGE',
}

export const useStreamSummary = () => {
  const [data, setData] = useState('')
  const [status, setStatus] = useState<StreamSummaryStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  const eventSourceRef = useRef<EventSource | null>(null)
  const dataRef = useRef('')
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const requestIdRef = useRef(0)

  const clearStreamTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  const cancel = useCallback(() => {
    requestIdRef.current++
    clearStreamTimeout()
    if (eventSourceRef.current) {
      eventSourceRef.current.onerror = null
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }
    setStatus('cancelled')
  }, [clearStreamTimeout])

  const start = useCallback(
    (params: StreamSummaryParams) => {
      // Close previous stream if active
      clearStreamTimeout()
      if (eventSourceRef.current) {
        eventSourceRef.current.onerror = null
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }

      const requestId = ++requestIdRef.current

      // Reset state
      setData('')
      setError(null)
      setStatus('streaming')
      dataRef.current = ''
      // 20 minute timeout
      timeoutRef.current = setTimeout(() => {
        if (requestIdRef.current !== requestId) return
        if (eventSourceRef.current) {
          eventSourceRef.current.onerror = null
          eventSourceRef.current.close()
          eventSourceRef.current = null
        }
        if (dataRef.current) {
          setStatus('done')
        } else {
          setStatus('error')
          setError('Stream timed out. Please try again.')
        }
      }, 20 * 60 * 1000)

      const source = new EventSource(`${window.GR_API_URLS.API_URL_LIVE_EVENTS_PREDICTIONS}/i/summary/stream/`, {
        fetch: async (input, init) => {
          await prepareDefaultHeaders()
          return fetch(input, {
            ...init,
            method: 'POST',
            headers: {
              ...init?.headers,
              Authorization: `Bearer ${keycloakService.token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              gameId: params.gameId,
              prompt: params.prompt,
              promptVersion: params.promptVersion,
              ...(params.extraInstructions ? { extraInstructions: params.extraInstructions } : {}),
              ...(params.asOfDate ? { asOfDate: params.asOfDate } : {}),
            }),
          })
        },
      })

      eventSourceRef.current = source

      source.onmessage = (event) => {
        if (requestIdRef.current !== requestId) return
        try {
          const chunk: { type: string; data: string } = JSON.parse(event.data)
          if (chunk.type === StreamDataType.TEXT) {
            dataRef.current += chunk.data
            setData(DOMPurify.sanitize(marked.parse(dataRef.current) as string))
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Failed to parse SSE chunk:', e)
        }
      }

      source.onerror = () => {
        if (requestIdRef.current !== requestId) return
        clearStreamTimeout()
        source.close()
        eventSourceRef.current = null
        if (dataRef.current) {
          setStatus('done')
        } else {
          setStatus('error')
          setError('Stream connection failed. Please try again.')
        }
      }
    },
    [clearStreamTimeout]
  )

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearStreamTimeout()
      eventSourceRef.current?.close()
    }
  }, [clearStreamTimeout])

  return { data, status, error, start, cancel }
}
